import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: number;
  color: string;
  bgGradient: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon, 
  trend, 
  trendValue, 
  color, 
  bgGradient, 
  subtitle 
}) => (
  <div className={`group relative overflow-hidden rounded-xl ${bgGradient} p-4 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-102 hover:-translate-y-1 border border-white/10`}>
    {/* Animated background elements */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="absolute -right-6 -bottom-6 opacity-20 group-hover:opacity-30 transition-opacity duration-500">
      <div className="w-16 h-16 rounded-full bg-white/20 group-hover:scale-110 transition-transform duration-500" />
    </div>
    <div className="absolute -left-4 -top-4 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
      <div className="w-12 h-12 rounded-full bg-white/30 group-hover:scale-125 transition-transform duration-500" />
    </div>
    
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1">
          <p className="text-white/90 text-xs font-semibold mb-1 tracking-wide uppercase">{title}</p>
          <p className="text-2xl font-bold mb-1 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
            {value}
          </p>
          {subtitle && (
            <p className="text-white/70 text-xs mb-1 font-medium">{subtitle}</p>
          )}
          {trend && trendValue !== undefined && (
            <div className="flex items-center space-x-1 bg-white/10 rounded-full px-2 py-0.5 backdrop-blur-sm">
              {trendValue > 0 ? (
                <TrendingUp className="h-3 w-3 text-green-300" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-300" />
              )}
              <span className="text-white/90 text-xs font-semibold">
                {trendValue > 0 ? '+' : ''}{trendValue}% {trend}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color} bg-white/25 backdrop-blur-md group-hover:bg-white/30 group-hover:scale-105 transition-all duration-300 shadow-lg`}>
          <div className="group-hover:rotate-6 transition-transform duration-300">
            {icon}
          </div>
        </div>
      </div>
      
      {/* Progress bar for visual appeal */}
      <div className="w-full bg-white/10 rounded-full h-1 overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-1000 group-hover:animate-pulse`} 
             style={{ width: '85%' }} />
      </div>
    </div>
  </div>
);

export default StatCard;
